import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';

import {
    CellClickedEvent,
    CellContextMenuEvent,
    CellDoubleClickedEvent,
    CellEditingStoppedEvent,
    CellValueChangedEvent,
    ColDef,
    ColumnApi,
    ExcelStyle,
    FirstDataRenderedEvent,
    GetContextMenuItemsParams,
    GridApi,
    GridReadyEvent,
    IServerSideDatasource,
    MenuItemDef,
    ModelUpdatedEvent,
    PaginationChangedEvent,
    RowClickedEvent,
    RowDataUpdatedEvent,
    RowDoubleClickedEvent,
    RowEditingStoppedEvent,
    RowValueChangedEvent,
    SelectionChangedEvent,
} from 'ag-grid-community';
import { SettingPanelService } from './setting-panel/setting-panel.service';
import { TDGridSettingsService } from './tdgrid-settings.service';
import { Observable, Subscription } from 'rxjs';
import { AISearchPanelService } from './ai-search-panel/ai-search-panel.service';

@Component({
    selector: 'td-grid',
    templateUrl: './td-grid.component.html',
    styleUrls: ['./td-grid.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SettingPanelService, TDGridSettingsService, AISearchPanelService],
})
export class TdGridComponent implements OnInit, OnDestroy {
    @ViewChild('grid', { static: false }) grid: AgGridAngular | undefined;

    private gridApi: GridApi | undefined;
    private columnApi: ColumnApi | undefined;
    private settingsSubscription: Subscription = new Subscription();
    private currentGridId: string = '';
    private originalColumnDefs: ColDef[] = [];
    private settingsLoaded: boolean = false;
    private aiSearchSubscription: Subscription = new Subscription();

    @HostBinding('class') classes = 'w-100 h-100 ag-theme-quartz';

    @HostBinding('class.settings-loading') get isSettingsLoading() {
        return !this.settingsLoaded && !!this.id;
    }

    @Input() id: string | undefined;
    @Input() keyExpr = 'id';
    @Input() parentKeyExpr = 'parentId';
    @Input() treeDisplayExpr = 'name';
    @Input() disableCheckboxExpr?: string = undefined;
    @Input() disableCheckboxConditionCheck?: any = false;
    @Input() activeCheckboxExpr?: string = undefined;
    @Input() activeCheckboxConditionCheck?: any = true;
    @Input() set columnDefs(value: ColDef[] | undefined) {
        // Store original column definitions for reset functionality
        if (value && value.length > 0) {
            this.originalColumnDefs = [...value];
        }
        this._columnDefs = value;
    }

    get columnDefs(): ColDef[] | undefined {
        return this._columnDefs;
    }

    private _columnDefs: ColDef[] | undefined;
    @Input() autoGroupColumnDef: ColDef | undefined;
    @Input() defaultColDef: ColDef | undefined;
    @Input() rowData: any[] = [];
    @Input() rowSelection: string | undefined = 'single'; //'single' | 'multiple'
    @Input() suppressRowClickSelection: boolean | undefined = false;
    @Input() suppressNoRowsOverlay: boolean | undefined = false;
    @Input() rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' | undefined;
    @Input() suppressDragLeaveHidesColumns: boolean | undefined = true;
    @Input() rowMultiSelectWithClick: boolean | undefined = false;
    @Input() pagination: boolean | undefined = true;
    @Input() paginationPageSize: number = 10;
    @Input() paginationPageSizeSelector: any = [10, 20, 30];
    @Input() rowModelType: string | undefined = 'clientSide'; // 'serverSide' : 'infinite' : 'clientSide'
    @Input() serverSideDatasource: IServerSideDatasource | undefined;
    @Input() enableRtl: boolean | undefined = false;
    @Input() animateRows: boolean | undefined = true;
    @Input() maxBlocksInCache: number = 0; // 0 means disable buffering in ag-grid
    @Input() rowHeight: number = 45;
    @Input() headerHeight: number = 40;
    @Input() enableRangeSelection: boolean | undefined = true;
    // enables undo / redo
    @Input() undoRedoCellEditing: boolean | undefined = true;
    @Input() enableFillHandle: boolean | undefined = false;
    @Input() undoRedoCellEditingLimit: number = 20;
    @Input() cacheOverflowSize: number = 0;
    // enables flashing to help see cell changes
    @Input() enableCellChangeFlash: boolean | undefined = true;
    @Input() groupSelectsChildren: boolean | undefined = false;
    @Input() groupDefaultExpanded: number | undefined = undefined; //-1 means expand all
    @Input() suppressClickEdit: boolean | undefined = false;
    @Input() editType: 'fullRow' | undefined = 'fullRow';
    @Input() singleClickEdit: boolean | undefined = false;
    @Input() checkboxSelection: boolean | undefined = false;
    @Input() treeData: boolean | undefined = false;
    @Input() excelStyles: ExcelStyle[] | undefined;
    @Input() hasExcelExport: boolean | undefined = true;
    @Input() haschartExport: boolean | undefined = true;
    @Input() localeText: { [key: string]: string };
    @Input() gridOptions: any;
    @Input() aiSearchQuery: string = '';

    contextMenuMultiItemsValue: MenuItemDef[] | MenuItemDefExtra[] = [];
    @Input() get contextMenuMultiItems() {
        return this.contextMenuMultiItemsValue;
    }

    set contextMenuMultiItems(val: MenuItemDef[] | MenuItemDefExtra[]) {
        this.contextMenuMultiItemsValue = val;
        this.cdr.detectChanges();
    }
    @Input() contextMenuItems: MenuItemDef[] | MenuItemDefExtra[] = [];
    // if set to true ths pagination status bar will remove
    @Input() suppressPaginationPanel: boolean | undefined = false;

    @Output() gridReady: EventEmitter<any> = new EventEmitter<GridReadyEvent>();
    @Output() cellClicked: EventEmitter<CellClickedEvent> = new EventEmitter<CellClickedEvent>();
    @Output() paginationChanged: EventEmitter<PaginationChangedEvent> = new EventEmitter<PaginationChangedEvent>();
    @Output() rowDataUpdated: EventEmitter<RowDataUpdatedEvent> = new EventEmitter<RowDataUpdatedEvent>();
    @Output() firstDataRendered: EventEmitter<FirstDataRenderedEvent> = new EventEmitter<FirstDataRenderedEvent>();
    @Output() cellDoubleClicked: EventEmitter<CellDoubleClickedEvent> = new EventEmitter<CellDoubleClickedEvent>();
    @Output() rowClicked: EventEmitter<RowClickedEvent> = new EventEmitter<RowClickedEvent>();
    @Output() rowDoubleClicked: EventEmitter<RowDoubleClickedEvent> = new EventEmitter<RowDoubleClickedEvent>();
    @Output() cellContextMenu: EventEmitter<CellContextMenuEvent> = new EventEmitter<CellContextMenuEvent>();
    @Output() rowSelected: EventEmitter<any> = new EventEmitter<any>();
    @Output() selectionChanged: EventEmitter<{
        data: any[];
        event: SelectionChangedEvent;
        isChangedByUser: boolean;
    }> = new EventEmitter<{
        data: any[];
        event: SelectionChangedEvent;
        isChangedByUser: boolean;
    }>();

    @Output() cellValueChanged: EventEmitter<CellValueChangedEvent> = new EventEmitter<CellValueChangedEvent>();
    @Output() rowValueChanged: EventEmitter<RowValueChangedEvent> = new EventEmitter<RowValueChangedEvent>();
    @Output() cellEditingStopped: EventEmitter<CellEditingStoppedEvent> = new EventEmitter<CellEditingStoppedEvent>();
    @Output() rowEditingStopped: EventEmitter<RowEditingStoppedEvent> = new EventEmitter<RowEditingStoppedEvent>();
    @Output() modelUpdated: EventEmitter<ModelUpdatedEvent> = new EventEmitter<ModelUpdatedEvent>();
    @Output() dataSourceChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() currentContextParamsChanged: EventEmitter<any> = new EventEmitter<GetContextMenuItemsParams>();
    @Output() aiSearchQueryApplied: EventEmitter<string> = new EventEmitter<string>();

    constructor(
        private readonly cdr: ChangeDetectorRef,
        private settingsPanelService: SettingPanelService,
        private tdGridSettingsService: TDGridSettingsService,
        private aiSearchPanelService: AISearchPanelService,
    ) {}

    ngOnInit(): void {
        this.settingsSubscription = this.settingsPanelService.action$.subscribe((action) => {
            this.handleSettingsAction(action);
        });
        this.aiSearchSubscription = this.aiSearchPanelService.action$.subscribe((action) => {
            this.handleAISearchAction(action);
        });
    }

    ngOnDestroy(): void {
        if (this.currentGridId && this.gridApi) {
            this.tdGridSettingsService.cleanupGridListeners(this.currentGridId, this.gridApi);
        }
        this.settingsSubscription.unsubscribe();
        this.aiSearchSubscription.unsubscribe();
    }

    getSelectedRowData() {
        const selectedData = this.gridApi?.getSelectedRows();
        return selectedData;
    }
    selectionChangedByUser: boolean = false;
    onlySelectGivenItems(selectedItems: any[]) {
        this.selectionChangedByUser = true;
        this.gridApi?.forEachNode((node) => (selectedItems.includes(node.data[this.keyExpr]) ? node.setSelected(true) : node.setSelected(false)));
    }

    selectAll() {
        this.gridApi?.selectAll();
    }

    onSelectionChanged(event: SelectionChangedEvent) {
        const selectedData = this.gridApi?.getSelectedRows();
        const newEvent: any = {
            data: selectedData,
            event: event,
            isChangedByUser: this.selectionChangedByUser,
        };
        this.selectionChanged.emit(newEvent);
        this.selectionChangedByUser = false;
    }

    gridReadyExtra(e: any) {
        this.gridApi = e.api;
        this.columnApi = e.columnApi;

        // Store original column definitions for reset functionality
        if (this.columnDefs) {
            this.originalColumnDefs = [...this.columnDefs];
        }

        // Set current grid ID and load saved settings
        if (this.id) {
            this.currentGridId = this.id;
            // Load saved settings if available with async handling
            setTimeout(() => {
                this.loadGridSettingsAsync();
            }, 100);
        } else {
            // If no grid ID, mark settings as loaded immediately
            this.settingsLoaded = true;
            this.cdr.detectChanges();
        }

        this.initializeAISearchService();

        // e.api.sizeColumnsToFit();
        this.gridReady.emit(e);
        this.cdr.detectChanges();
    }

    public clearFilters() {
        this.gridApi?.setFilterModel(null);
        this.gridApi?.onFilterChanged();
    }

    public async firstDataRenderedExtra(params: FirstDataRenderedEvent) {
        this.firstDataRendered.emit(params);
    }

    public rowClickedExtra(params: any) {
        this.rowClicked.emit(params);
    }

    /**
     * Handle settings panel actions (save/reset)
     */
    private handleSettingsAction(action: { action: 'save' | 'reset' }): void {
        if (!this.gridApi) return;

        switch (action.action) {
            case 'save':
                this.saveGridSettingsAsync();
                break;
            case 'reset':
                this.resetGridSettingsAsync();
                break;
        }
    }
    /**
     * Reset grid to default state
     */
    private resetGridToDefault(): void {
        if (!this.gridApi) return;

        // Use stored original column definitions for reset
        if (this.originalColumnDefs.length > 0) {
            // Create fresh column definitions with default visibility
            const resetColumnDefs = this.originalColumnDefs.map((col) => ({
                ...col,
                width: col.width || 100, // Reset to default width if specified
                minWidth: col.minWidth || 5, // Reset to default minWidth if specified
                sort: undefined, // Clear any sorting
            }));

            // Apply the reset column definitions
            this.gridApi.setColumnDefs(resetColumnDefs);

            // Force all columns to be visible by applying column state
            this.gridApi.applyColumnState({
                state: [],
                defaultState: {
                    hide: false,
                    pinned: null,
                },
            });
        }

        // Reset pagination to default
        if (this.paginationPageSize) {
            this.gridApi.paginationSetPageSize(this.paginationPageSize);
        }

        // Clear all filters
        this.gridApi.setFilterModel(null);
        this.gridApi.onFilterChanged();

        // Clear sorting by resetting column state
        this.gridApi.applyColumnState({
            state: [],
            defaultState: {
                sort: null,
                sortIndex: null,
                pinned: null,
            },
        });

        // Force grid refresh to ensure changes are applied
        this.gridApi.refreshCells();

        // Ensure columns are properly sized after reset
        setTimeout(() => {
            this.gridApi?.autoSizeAllColumns();
        }, 100);

        // Trigger change detection
        this.cdr.detectChanges();
    }

    /**
     * Load saved grid settings if available
     */
    public loadSavedSettings(gridId: string): void {
        if (this.currentGridId !== gridId) {
            this.currentGridId = gridId;
            this.settingsLoaded = false; // Reset settings loaded state

            // Load saved settings for the new grid ID
            if (this.gridApi) {
                this.loadGridSettingsAsync(gridId);
            } else {
                // If no grid API yet, mark as loaded
                this.settingsLoaded = true;
                this.cdr.detectChanges();
            }
        }
    }
    /**
     * Update column definitions and store original for reset functionality
     */
    public updateColumnDefinitions(newColumnDefs: ColDef[]): void {
        if (newColumnDefs && newColumnDefs.length > 0) {
            this.originalColumnDefs = [...newColumnDefs];
            this._columnDefs = [...newColumnDefs];

            if (this.gridApi) {
                // Use centralized method to set column definitions
                this.tdGridSettingsService.setColumnDefs(this.gridApi, newColumnDefs, this.currentGridId).subscribe();
            }
        }
    }

    /**
     * Load grid settings asynchronously
     */
    private loadGridSettingsAsync(gridId?: string): void {
        const targetGridId = gridId || this.currentGridId;
        if (!targetGridId || !this.gridApi) {
            // If no grid ID or API, mark as loaded immediately
            this.settingsLoaded = true;
            this.cdr.detectChanges();
            return;
        }

        console.log(`🔄 Loading grid settings for: ${targetGridId}`);

        this.tdGridSettingsService.applyGridSettings(this.gridApi, targetGridId).subscribe({
            next: (success) => {
                this.settingsLoaded = true;
                if (success) {
                    console.log(`✅ Grid settings loaded and applied for: ${targetGridId}`);
                } else {
                    console.log(`ℹ️ No settings to apply for: ${targetGridId}`);
                }
                // Refresh the grid to ensure data is displayed properly
                this.refreshDataAfterSettingsLoaded();
                this.settingsPanelService.triggerRefresh();
            },
            error: (error) => {
                this.settingsLoaded = true; // Still mark as loaded even on error
                console.error(`❌ Error loading grid settings for: ${targetGridId}`, error);
                // Still refresh the grid even on error
                this.refreshDataAfterSettingsLoaded();
            },
        });
    }

    /**
     * Save grid settings asynchronously
     */
    private saveGridSettingsAsync(): void {
        if (!this.gridApi || !this.currentGridId) return;

        console.log(`💾 Saving grid settings for: ${this.currentGridId}`);
        this.settingsPanelService.setSaveLoading(true);
        this.tdGridSettingsService.saveGridSettings(this.gridApi, this.currentGridId).subscribe({
            next: (result) => {
                if (result.success) {
                    console.log(`✅ Grid settings saved successfully for: ${this.currentGridId}`);
                } else {
                    console.log(`❌ Failed to save grid settings for: ${this.currentGridId}`);
                }
                this.settingsPanelService.setSaveLoading(false);
                this.cdr.detectChanges();
            },
            error: (error) => {
                console.error(`❌ Error saving grid settings for: ${this.currentGridId}`, error);
                this.settingsPanelService.setSaveLoading(false);
                this.cdr.detectChanges();
            },
        });
    }
    /**
     * Reset grid settings asynchronously
     */
    private resetGridSettingsAsync(): void {
        if (!this.currentGridId) return;

        console.log(`🔄 Resetting grid settings for: ${this.currentGridId}`);
        this.settingsPanelService.setResetLoading(true);
        // First reset the grid to default state
        this.tdGridSettingsService.resetGridSettings(this.currentGridId, this.gridApi).subscribe({
            next: (result) => {
                if (result.success) {
                    console.log(`✅ Grid settings reset successfully for: ${this.currentGridId}`);
                } else {
                    console.log(`ℹ️ No settings to reset for: ${this.currentGridId}`);
                }
                this.settingsPanelService.setResetLoading(false);
                this.cdr.detectChanges();
                this.settingsPanelService.triggerRefresh();
            },
            error: (error) => {
                console.error(`❌ Error resetting grid settings for: ${this.currentGridId}`, error);
                this.settingsPanelService.setResetLoading(false);
                this.cdr.detectChanges();
            },
        });
    }

    /**
     * Get loading state for the current grid
     */
    public getLoadingState(): Observable<boolean> {
        return this.tdGridSettingsService.getLoadingState(this.currentGridId);
    }

    /**
     * Get error state for the current grid
     */
    public getErrorState(): Observable<string | null> {
        return this.tdGridSettingsService.getErrorState(this.currentGridId);
    }

    /**
     * Check if settings are loaded (for template use)
     */
    public get isSettingsLoaded(): boolean {
        return this.settingsLoaded;
    }

    /**
     * Manually trigger data refresh after settings are loaded
     */
    public refreshDataAfterSettingsLoaded(): void {
        if (this.settingsLoaded && this.gridApi) {
            // Force grid to refresh and show data
            this.gridApi.refreshCells();
            this.gridApi.redrawRows();
            this.cdr.detectChanges();
        }
    }
    private initializeAISearchService(): void {
        if (this.gridApi && this.gridApi.getAllGridColumns) {
            const columns = this.gridApi.getAllGridColumns().filter((col) => col.getColDef().headerName && col.getColId() !== 'ag-Grid-AutoColumn');

            this.aiSearchPanelService.setColumnMappings(columns);

            // Set the current AI search query state if it exists
            if (this.aiSearchQuery && this.aiSearchQuery.trim()) {
                this.aiSearchPanelService.setAppliedQuery(this.aiSearchQuery);
            }
        }
    }

    private handleAISearchAction(action: { action: 'applyQuery' | 'clearQuery'; query?: string }): void {
        switch (action.action) {
            case 'applyQuery':
                if (action.query) {
                    this.aiSearchQueryApplied.emit(action.query);
                }
                break;
            case 'clearQuery':
                this.aiSearchQueryApplied.emit('');
                break;
        }
    }
}

export class MenuItemDefExtra {
    type?: string;
    subMenu?: (string | MenuItemDef)[] | undefined;
    name?: string;
    disabled?: boolean | undefined;
    shortcut?: string | undefined;
    action?: ((e: GetContextMenuItemsParams) => void) | undefined;
    ignore?: ((e: GetContextMenuItemsParams) => boolean) | undefined;
    checked?: boolean | undefined;
    icon?: string | Element | undefined;
    cssClasses?: string[] | undefined;
    tooltip?: string | undefined;
}
